import React, { useState, useMemo, useEffect } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { useToast } from '../../components/Toast/ToastContext';
import Modal, { ModalTitle, ModalActions } from '../../components/Modal/Modal';
import Button from '../../components/Button/Button';
import { PortalField } from '../../components/FormField/PortalField';
import { PortalInput } from '../../components/FormField/PortalInput';
import { PortalSelect } from '../../components/FormField/PortalSelect';
import { assignDeviceMutation, rescuePetsQuery } from '../../lib/rescueQueries';
import type { RescuePetsData } from '../../lib/rescueQueries';
import styles from './Pets.module.scss';

interface AssignDeviceModalProps {
  open: boolean;
  onClose: () => void;
  initialPetId?: string;
}

export default function AssignDeviceModal({ open, onClose, initialPetId }: AssignDeviceModalProps) {
  const { showToast } = useToast();
  const [moduleId, setModuleId] = useState('');
  const [petId, setPetId] = useState('');
  const [moduleError, setModuleError] = useState('');

  const { data: petsData, loading: petsLoading } = useQuery<RescuePetsData>(rescuePetsQuery);

  const pets = useMemo(() => {
    if (!petsData) {
      return [];
    }
    const rescueHousehold = petsData.currentUser?.userHouseholds?.find((uh) => uh.household.type === 'RESCUE');
    const allPets = rescueHousehold?.household.pets ?? [];
    return allPets.filter((pet) => !pet.device);
  }, [petsData]);

  useEffect(() => {
    if (!open) {
      return;
    }
    if (initialPetId && pets.some((p) => p.id === initialPetId)) {
      setPetId(initialPetId);
    } else if (pets.length > 0 && !petId) {
      setPetId(pets[0].id);
    }
  }, [open, initialPetId, pets]);

  const [assignDevice, { loading: assigning }] = useMutation(assignDeviceMutation, {
    refetchQueries: 'active' as const,
  });

  function handleClose() {
    setModuleId('');
    setPetId('');
    setModuleError('');
    onClose();
  }

  async function handleActivate() {
    if (!moduleId.trim()) {
      setModuleError('Device ID is required');
      return;
    }
    if (!petId) {
      return;
    }
    try {
      await assignDevice({
        variables: { moduleId: moduleId.trim().toUpperCase(), petId },
      });
      showToast(`Device ${moduleId.trim().toUpperCase()} activated`);
      handleClose();
    } catch (err) {
      setModuleError(err instanceof Error ? err.message : 'Failed to activate device');
    }
  }

  const canSubmit = moduleId.trim().length > 0 && petId.length > 0 && !assigning;

  return (
    <Modal open={open} onClose={handleClose}>
      <ModalTitle>Assign Fi Device</ModalTitle>
      <p className={styles.modalDescription}>Enter the device&rsquo;s module ID and select a pet to assign it to.</p>

      <PortalField label="Device ID" error={moduleError} showError={!!moduleError}>
        <PortalInput
          value={moduleId}
          onChange={(e) => {
            setModuleId(e.target.value);
            setModuleError('');
          }}
          onKeyDown={(e) => e.key === 'Enter' && handleActivate()}
          placeholder="e.g. M1XXXXXXXX"
          hasError={!!moduleError}
          autoFocus
        />
      </PortalField>

      <PortalField label="Pet">
        <PortalSelect value={petId} onChange={(e) => setPetId(e.target.value)} disabled={petsLoading}>
          {pets.map((pet) => (
            <option key={pet.id} value={pet.id}>
              {pet.name}
            </option>
          ))}
        </PortalSelect>
      </PortalField>

      <ModalActions>
        <Button variant="secondary" onClick={handleClose}>
          Cancel
        </Button>
        <Button variant="green" onClick={handleActivate} disabled={!canSubmit}>
          {assigning ? 'Activating...' : 'Activate Device'}
        </Button>
      </ModalActions>
    </Modal>
  );
}
